import type { LangCode } from "./config";

// AI-translated fallbacks (generated by `node scripts/translate-ui.mjs`).
// Manual dictionaries below override these key-by-key.
import GEN_FI from "./generated/fi.json";

/**
 * UI string dictionary. Add new keys here and they become typed across the app.
 * EN is the source of truth. All 12 languages are now hand-populated; missing
 * keys still fall back to EN via the `t()` helper in LanguageContext.
 */
export const UI_EN = {
  // Top nav
  "nav.find": "Find",
  "nav.best": "Best",
  "nav.todo": "Things to do",
  "nav.events": "Events",
  "nav.submitEvent": "Submit an event",
  "nav.restaurants": "Restaurants",
  "nav.signin": "Sign in",
  "nav.search": "Search",
  "nav.city": "City",
  "nav.chooseCity": "Choose city",
  "nav.finland": "Finland",
  "nav.chooseDestination": "Choose destination",
  "map.places": "Places",
  "map.destinations": "Destinations",
  "nav.language": "Language",
  "nav.chooseLanguage": "Choose language",

  // Bottom nav
  "bottom.home": "Home",
  "bottom.eat": "Eat",
  "bottom.discover": "Discover",
  "bottom.events": "Events",
  "bottom.saved": "Saved",

  // Section meta
  "section.eat.title": "What to eat",
  "section.eat.eyebrow": "Restaurants, cafes & bars",
  "section.eat.subtitle": "Curated tables — from corner cafes to Michelin-starred kitchens.",
  "section.do.title": "What to do",
  "section.do.eyebrow": "Experiences & shopping",
  "section.do.subtitle": "Handpicked places to wander, swim, shop, and breathe.",
  "section.happening.title": "What's happening",
  "section.happening.eyebrow": "Events, gigs & shows",
  "section.happening.subtitle": "The week's most worthy concerts, exhibitions, and cultural moments.",

  // Listing chrome
  "listing.of": "of",
  "listing.empty.title": "Nothing here yet",
  "listing.empty.body": "Try a different filter — or another city.",
  "listing.searchEat": "Search more places to eat",
  "listing.searchDo": "Search more things to do",
  "listing.searchHappening": "Search more happenings",
  "listing.cta.title": "Find the best things to eat, do & experience in Finland",
  "listing.cta.body": "what is your insider key to Finland's tables, hidden corners and cultural moments — handpicked, locally loved, ready to unlock.",
  "listing.view.list": "List",
  "listing.view.map": "Map",
  "listing.sort.nearest": "Nearest",
  "listing.sort.featured": "Featured",
  "listing.sort.cta": "Sort by nearest",
  "listing.geo.locating": "Locating…",
  "listing.geo.off": "Location off",
  "listing.geo.show": "Show my location",
  "listing.map.hint": "Tap any pin to see photo & directions",

  // Tags / filters
  "tag.all": "All",
  "tag.featured": "Featured",
  "tag.local-fav": "Local Fav",
  "tag.new": "New",
  "tag.sauna": "Sauna",
  "tag.kid-friendly": "Kid-friendly",
  "tag.dog-friendly": "Dog-friendly",

  // Place card
  "card.reviews": "Reviews",
  "card.popular": "Popular",
  "card.trending": "Trending",
  "card.view": "View",
  "card.save": "Save",
  "card.unsave": "Remove from saved",
  "card.directions": "Directions",
  "card.menu": "Menu",
  "card.menuQr": "Menu QR code",

  // Sauna spotlight
  "sauna.eyebrow": "Helsinki essential",
  "sauna.title": "Sauna — the Finnish ritual",
  "sauna.body": "Public saunas, sea-pool spas, and wood-fired hideouts. Sweat, plunge, repeat — the way Helsinki has always done it.",
  "sauna.cta": "View saunas",

  // Footer
  "footer.tagline": "A curated weekly guide to Helsinki.",
  "footer.emailPlaceholder": "you@example.com",
  "footer.join": "Join",
  "footer.fine": "One short email every Thursday. No spam, unsubscribe anytime.",
  "footer.col.explore": "Explore",
  "footer.col.cities": "Cities",
  "footer.col.company": "Company",
  "footer.privacy": "Privacy",
  "footer.terms": "Terms",
  "footer.rights": "© 2026 what Finland Oy",
  "footer.link.press": "Press",
  "footer.link.partner": "Partner",
  "footer.link.contact": "Contact",

  // Contact page
  "contact.field.name": "Full name",
  "contact.field.name.placeholder": "Your name",
  "contact.field.company": "Company",
  "contact.field.optional": "Optional",
  "contact.field.email": "Email",
  "contact.field.phone": "Phone",
  "contact.field.message": "Message",
  "contact.field.message.placeholder": "Tell us a bit more…",
  "contact.field.subject": "What can we help with?",
  "contact.subject.general": "General Inquiry",
  "contact.subject.partnership": "Business Partnership",
  "contact.subject.media": "Media & Press",
  "contact.subject.support": "Technical Support",
  "contact.submit": "Send message",
  "contact.agree": "By sending, you agree to be contacted by what Finland Oy.",
  "contact.sent": "Opening your email app… if nothing happened, write directly to",
  "contact.error.name": "Please enter your name.",
  "contact.error.name.long": "Name is too long.",
  "contact.error.email.required": "Email is required.",
  "contact.error.email.invalid": "Email looks invalid.",
  "contact.error.email.long": "Email is too long.",
  "contact.error.company.long": "Company name is too long.",
  "contact.error.phone.long": "Phone is too long.",
  "contact.error.message.required": "Message can't be empty.",
  "contact.error.message.long": "Message is too long (max 2000).",

  // Hero (home)
  "hero.live": "Live",
  "hero.spots": "120+ handpicked spots in",
  "hero.badge": "Unlock Finland",
  "hero.line1": "Unlock the city.",
  "hero.line2": "Discover what to eat, do",
  "hero.line3a": "& experience in",
  "hero.line3b": "Finland",
  "hero.lead": "Find places, restaurants and things to do — handpicked by locals, ready to unlock in seconds.",
  "hero.input.placeholder": "Find places and things to do",
  "hero.search": "Search",
  "hero.pill.eat": "What to eat",
  "hero.pill.do": "What to do",
  "hero.pill.happening": "What's happening",
  "hero.stat.spots": "Curated spots",
  "hero.stat.rating": "Avg. rating",
  "hero.stat.cities": "Around Finland",
  "hero.scroll": "Scroll",

  // Home sections
  "home.tophot": "What's hot in",
  "home.unlockmore": "Discover what else",
  "loadmore.show": "Show More",
  "loadmore.loading": "Loading",
  "loadmore.end": "End of list",
  "home.near": "What's near you",
  "home.cal.eyebrow": "· What's on the calendar",
  "home.cal.title": "Upcoming events",
  "home.cal.in": "in",
  "home.cal.viewall": "View all",
  "home.cal.unlockall": "Unlock all events",
  "home.coming": "Coming soon.",
  "home.aurora.country": "Finland",
  "home.aurora.p1": "what is your ultimate digital insider to the best spots in town. No endless scrolling, just curated vibes, handpicked flavors, and unforgettable experiences. Whether you're looking for the coolest hidden coffee shops, local-approved weekend events, or the best sauna spots around you, we've done the homework so you don't have to.",
  "home.aurora.p2": "From the heart of Helsinki to the next hidden gems across Finland (and beyond), we bring the city to your screen. Your next adventure starts with one question: what's next?",

  // Common
  "common.featured": "Featured",
  "common.tryagain": "Try again",
  "common.gohome": "Go home",
  "common.prev": "Previous",
  "common.next": "Next",

  // Error pages (root)
  "err.notfound.title": "Page not found",
  "err.notfound.body": "The page you're looking for doesn't exist or has been moved.",
  "err.notfound.back": "Back home",
  "err.boundary.title": "This page didn't load",
  "err.boundary.body": "Something went wrong on our end.",

  // Place detail
  "place.reviews": "Reviews",
  "place.write": "Write a review",
  "place.reserve": "Make a reservation",
  "place.hideMap": "Hide map",
  "place.showMap": "Locations",
  "place.openMaps": "Open in Google Maps",
  "place.related": "You may also like",
  "place.unlock.table": "Unlock restaurant",
  "place.unlock.activity": "Unlock activity",
  "place.unlock.tickets": "Unlock tickets",
  "place.unlock.now": "now",
  "place.unlock.label": "Unlock",
  "place.unlock.website": "Visit website",
  "place.notfound.title": "Not found",
  "place.notfound.body": "This place doesn't exist (yet).",
  "place.notfound.back": "Back to listings",
  "place.error.title": "Something went wrong",
  "place.nearby": "Nearby",
  "place.promo.eat": "Step inside the heart of the city!",
  "place.promo.do": "An unforgettable experience awaits!",
  "place.promo.happening": "Don't miss this happening!",
  "place.promo.body": "Treat yourself to something special — locals, friends, or family, this is a moment worth unlocking.",
  "place.body.tucked": "Tucked into the heart of",
  "place.body.tail": "blends local craft with a sense of place. Whether you're stopping in for a quick visit or making a full afternoon of it, expect attention to detail, warm hospitality, and the kind of moment you'll want to come back for.",
  "place.body.hand": "Handpicked by what Finland Oy — every spot in our guide is locally loved, quietly tested, and ready to be unlocked.",
  "place.priceFrom": "from 29€",
  "place.tagline.eat": "A taste of {area} you won't forget",
  "place.tagline.do": "An experiential moment in {area}",
  "place.tagline.happening": "Live in {area} — don't miss it",
  "place.tab.overview": "Overview",
  "place.tab.details": "Details",
  "place.tab.tickets": "Tickets",
  "place.tab.schedule": "Schedule",
  "place.tab.reviews": "Reviews",
  "place.tabShort.overview": "Info",
  "place.tabShort.details": "More",
  "place.tabShort.tickets": "Book",
  "place.tabShort.schedule": "Hours",
  "place.tabShort.reviews": "Reviews",
  "place.saved": "Saved",
  "place.save": "Save",
  "place.search.eat": "Search more places to eat",
  "place.search.do": "Search more things to do",
  "place.search.happening": "Search more happenings",

  // Cities
  "cities.choose": "Choose your city",
  "cities.title": "Cities",
  "cities.lead": "Curated by locals, mapped in real time. Tap a pin or a card to switch your guide.",
  "cities.current": "Currently",
  "cities.liveMap": "Live Map",
  "cities.now": "Now exploring · est.",
  "cities.people": "People",
  "cities.region": "Region",
  "cities.spots": "Spots",
  "cities.jump.eat": "Eat",
  "cities.jump.do": "Do",
  "cities.jump.live": "On now",
  "cities.all": "All cities",
  "cities.tap": "Tap to switch",
  "cities.selected": "Selected",
  "cities.select": "Select",
  "cities.curatedSpot": "curated spot",
  "cities.curatedSpots": "curated spots",
  "cities.founded": "Founded",
  "cities.explore": "Explore",
  "cities.hub": "City hub",
  "cities.directory": "Directory",
  "cities.regions": "Regions",
  "cities.fullMap": "Full map",
  "cities.finland": "Finland",

  // Saved
  "saved.eyebrow": "Your collection",
  "saved.title": "Saved",
  "saved.empty": "No saves yet",
  "saved.emptyBody": "Tap the heart on any place, experience or event to keep it here for later.",
  "saved.start": "Start browsing",

  // Do experience page
  "do.eyebrow": "Things to do",
  "do.title.line1": "Spend the day",
  "do.title.line2": "like a local.",
  "do.lead": "Local walks, waterside spots, markets and quiet corners — the moments that make {city} feel like yours.",
  "do.stat.exp": "Experiences",
  "do.stat.saunas": "Saunas",
  "do.stat.rating": "Avg. rating",
  "do.group.all": "Everything",
  "do.group.sauna": "Sauna & sea",
  "do.group.arch": "Architecture",
  "do.group.outdoor": "Outdoor & islands",
  "do.group.shop": "Shopping",
  "do.spotlight.eyebrow": "Helsinki essential",
  "do.spotlight.title": "The sauna ritual",
  "do.spotlight.body": "Wood-fired hideouts, sea-pool spas, public bathing temples — pick your sweat.",
  "do.section.eyebrow": "Handpicked",
  "do.section.all": "Everything to do",
  "do.empty.title": "nothing in this lane yet",
  "do.empty.body": "Try another filter.",
  "do.cta.eyebrow": "Keep exploring",
  "do.cta.title": "Hungry yet?",
  "do.cta.body": "Pair your day with the city's best tables, bakeries and bars.",
  "do.cta.link": "Where to eat",
  "do.feature.discover": "Discover",

  // Happening experience page
  "hap.eyebrow": "This week",
  "hap.title.line1": "What's happening",
  "hap.title.line2": "Tonight & beyond.",
  "hap.lead": "Festivals, gigs, biennials and the quiet jazz rooms — the cultural pulse of {city}, curated week by week.",
  "hap.stat.live": "Live events",
  "hap.stat.new": "New this week",
  "hap.stat.fest": "Festival season",
  "hap.stat.fest.value": "Aug",
  "hap.lane.all": "All happenings",
  "hap.lane.music": "Music & festivals",
  "hap.lane.art": "Art & exhibitions",
  "hap.lane.live": "Live & nightlife",
  "hap.section.eyebrow": "On the calendar",
  "hap.section.all": "Coming up",
  "hap.empty.title": "No events in this lane yet",
  "hap.empty.body": "Try another category.",
  "hap.cta.eyebrow": "Weekly drop",
  "hap.cta.title": "Never miss a Helsinki night.",
  "hap.cta.body": "Get the week's most worthy gigs, openings and ceremonies — straight from locals.",
  "hap.cta.link": "Plan your day too",
  "hap.local": "Local fav",
  "hap.new": "New",
  "hap.tickets": "Tickets",
  "hap.gettickets": "Get tickets",

  // Contact page extras
  "contact.eyebrow.reply": "We reply within 48h",
  "contact.eyebrow.in": "what Finland Oy · Get in touch",
  "contact.hero.line1": "Let's start",
  "contact.hero.line2": "a conversation.",
  "contact.lead": "For partnerships, press, or to tell us about a place we should know — drop a line. Every message is routed to the right person on our team.",
  "contact.tile.general": "General",
  "contact.tile.part": "Partnerships",
  "contact.tile.based": "Based in",
  "contact.tile.basedV": "Helsinki, Finland",
  "contact.tile.hours": "Hours",
  "contact.tile.hoursV": "Mon–Fri · 09–17 EET",
  "contact.side.eyebrow": "Why reach out",
  "contact.side.title.a": "The team behind",
  "contact.side.b1": "Partner with us on a campaign",
  "contact.side.b2": "Submit a place we should cover",
  "contact.side.b3": "Press, media or interview requests",
  "contact.side.b4": "Bug reports & feature ideas",
  "contact.side.follow": "Follow along",
  "contact.send.eyebrow": "Send a message",
  "contact.send.title": "Tell us what's on your mind",
  "contact.cta.eyebrow": "Prefer to browse first?",
  "contact.cta.title": "Explore what to eat, do and see.",
  "contact.cta.link": "Open guide",

  // Hero AI search + extras
  "hero.ask": "ask ai",
  "hero.ask.loading": "opening…",
  "hero.ask.aria": "ask what AI",
  "hero.rateGoogle": "rate us on google",
  "ai.prompt.gems": "Ask what AI — hidden gems in helsinki",
  "ai.prompt.dinner": "Ask what AI — best dinner spots tonight",
  "ai.prompt.sauna": "Ask what AI — a relaxing sauna session",
  "ai.prompt.weekend": "Ask what AI — what's happening this weekend",
  "ai.prompt.cafe": "Ask what AI — cozy cafés to work from",
  "ai.prompt.today": "Ask what AI — things to do today",

  // Home extras
  "home.sauna.title": "Sauna time, say whaaat 🔥",
  "home.sauna.cta": "Discover more saunas",
  "home.events.subtitle": "in",
  "home.spots.suffix": "in",
  "home.aurora.title": "Unlock the city, one vibe at a time",
  "home.aurora.subtitle": "Local secrets, curated drops, and AI that knows what you'll love — all in one place.",
  "home.hero.subheadline": "Curated restaurants, saunas and events.",
  "home.hero.badge": "120+ handpicked spots in",

  // Card extras
  "card.noreviews": "No reviews yet",
  "card.unlock": "Unlock",

  // Event extras
  "event.ongoing": "Ongoing",
  "event.allDay": "All day · Doors 18:00",
} as const;

export type UIKey = keyof typeof UI_EN;

const UI_FI: Partial<Record<UIKey, string>> = {
  "nav.find": "Löydä", "nav.best": "Parhaat", "nav.todo": "Tekemistä", "nav.events": "Tapahtumat",
  "nav.submitEvent": "Ilmoita tapahtuma",
  "nav.restaurants": "Ravintolat", "nav.signin": "Kirjaudu", "nav.search": "Etsi",
  "nav.city": "Kaupunki", "nav.chooseCity": "Valitse kaupunki", "nav.language": "Kieli", "nav.chooseLanguage": "Valitse kieli",
  "nav.finland": "Suomi", "nav.chooseDestination": "Valitse kohde",
  "map.places": "Paikat", "map.destinations": "Kohteet",
  "bottom.home": "Etusivu", "bottom.eat": "Syö", "bottom.discover": "Koe", "bottom.events": "Tapahtumat", "bottom.saved": "Tallennetut",
  "section.eat.title": "Mitä syödä", "section.eat.eyebrow": "Ravintolat, kahvilat & baarit",
  "section.eat.subtitle": "Käsin kuratoidut valinnat — kulmakahviloista Michelin-tähtien keittiöihin.",
  "section.do.title": "Mitä tehdä", "section.do.eyebrow": "Kokemukset & shoppailu",
  "section.do.subtitle": "Valitut paikat vaella, ui, shoppaile ja hengitä.",
  "section.happening.title": "Mitä tapahtuu", "section.happening.eyebrow": "Tapahtumat, keikat & näyttelyt",
  "section.happening.subtitle": "Viikon parhaat konsertit, näyttelyt ja kulttuurihetket.",
  "listing.of": "/", "listing.empty.title": "Ei vielä mitään", "listing.empty.body": "Kokeile toista suodatinta — tai toista kaupunkia.",
  "listing.searchEat": "Etsi lisää ruokapaikkoja", "listing.searchDo": "Etsi lisää tekemistä", "listing.searchHappening": "Etsi lisää tapahtumia",
  "listing.cta.title": "Löydä Suomen parhaat ravintolat, kokemukset ja elämykset",
  "listing.cta.body": "what on avaimesi Suomen parhaisiin ravintoloihin, piilopaikkoihin ja kulttuurihetkiin — käsin valittuja paikallisten suosikkeja.",
  "listing.view.list": "Lista", "listing.view.map": "Kartta",
  "listing.sort.nearest": "Lähin", "listing.sort.featured": "Suositellut", "listing.sort.cta": "Järjestä lähimmän mukaan",
  "listing.geo.locating": "Paikannetaan…", "listing.geo.off": "Sijainti pois päältä", "listing.geo.show": "Näytä sijaintini",
  "listing.map.hint": "Napauta nuppineulaa — kuva ja reittiohjeet",
  "tag.all": "Kaikki", "tag.featured": "Suositellut", "tag.local-fav": "Paikalliset", "tag.new": "Uusi", "tag.sauna": "Sauna", "tag.kid-friendly": "Lapsiystävällinen", "tag.dog-friendly": "Koiraystävällinen",

  "card.popular": "Suosittu", "card.trending": "Nousussa",
  "card.reviews": "Arvostelua", "card.view": "Katso", "card.save": "Tallenna", "card.unsave": "Poista tallennetuista", "card.directions": "Reittiohjeet", "card.menu": "Ruokalista", "card.menuQr": "Ruokalistan QR-koodi",
  "sauna.eyebrow": "Helsingin perusasia", "sauna.title": "Sauna — suomalainen perinnerituaali",
  "sauna.body": "Yleisiä saunoja, piilotettuja helmiä ja upeita uimapaikkoja. Heitä löylyä, pulahda, toista — kuten Helsingissä on aina tehty.",
  "sauna.cta": "Katso saunat",
  "footer.tagline": "Kattava viikoittainen opas Helsinkiin — tiedät missä syödä, juoda ja missä tapahtuu.",
  "footer.emailPlaceholder": "sina@esimerkki.fi", "footer.join": "Liity",
  "footer.fine": "Yksi lyhyt sähköposti torstaisin. Ei roskapostia, peru milloin vain.",
  "footer.col.explore": "Tutustu", "footer.col.cities": "Kaupungit", "footer.col.company": "Yritys",
  "footer.privacy": "Tietosuoja", "footer.terms": "Ehdot", "footer.rights": "© 2026 what Finland Oy",
  "footer.link.press": "Media", "footer.link.partner": "Kumppani", "footer.link.contact": "Yhteystiedot",
  "contact.field.name": "Koko nimi",
  "contact.field.name.placeholder": "Nimesi",
  "contact.field.company": "Yritys",
  "contact.field.optional": "Valinnainen",
  "contact.field.email": "Sähköposti",
  "contact.field.phone": "Puhelin",
  "contact.field.message": "Viesti",
  "contact.field.message.placeholder": "Kerro hieman lisää…",
  "contact.field.subject": "Missä voimme auttaa?",
  "contact.subject.general": "Yleinen tiedustelu",
  "contact.subject.partnership": "Yhteistyö",
  "contact.subject.media": "Media & lehdistö",
  "contact.subject.support": "Tekninen tuki",
  "contact.submit": "Lähetä viesti",
  "contact.agree": "Lähettämällä hyväksyt, että what Finland Oy ottaa yhteyttä.",
  "contact.sent": "Avataan sähköpostisovellustasi… jos mitään ei tapahtunut, kirjoita suoraan osoitteeseen",
  "contact.error.name": "Anna nimesi.",
  "contact.error.name.long": "Nimi on liian pitkä.",
  "contact.error.email.required": "Sähköposti vaaditaan.",
  "contact.error.email.invalid": "Sähköposti näyttää virheelliseltä.",
  "contact.error.email.long": "Sähköposti on liian pitkä.",
  "contact.error.company.long": "Yrityksen nimi on liian pitkä.",
  "contact.error.phone.long": "Puhelinnumero on liian pitkä.",
  "contact.error.message.required": "Viesti ei voi olla tyhjä.",
  "contact.error.message.long": "Viesti on liian pitkä (max 2000).",

  // Hero (home)
  "hero.live": "Live",
  "hero.spots": "120+ käsin valittua paikkaa kaupungissa",
  "hero.badge": "Avaa Suomi",
  "hero.line1": "Kaupungin parhaat",
  "hero.line2": "paikat, menot ja elämykset.",
  "hero.line3a": "",
  "hero.line3b": "",
  "hero.lead": "Kätketyt helmet, ajattomia klassikoita ja viihdyttävää tekemistä — paikallisten kuratoimia, valmiina sinua varten.",
  "hero.input.placeholder": "Etsi paikkoja ja tekemistä",
  "hero.search": "Etsi",
  "hero.pill.eat": "Mitä syödä",
  "hero.pill.do": "Mitä tehdä",
  "hero.pill.happening": "Mitä tapahtuu",
  "hero.stat.spots": "What suositusta",
  "hero.stat.rating": "Keskiarvo",
  "hero.stat.cities": "Ympäri Suomen",
  "hero.scroll": "Selaa",

  // Home sections
  "home.tophot": "Mikä on kuuminta kaupungissa",
  "home.unlockmore": "Katso lisää",
  "loadmore.show": "Näytä lisää",
  "loadmore.loading": "Ladataan",
  "loadmore.end": "Listan loppu",
  "home.near": "Mitä lähelläsi",
  "home.cal.eyebrow": "· Mitä kalenterissa",
  "home.cal.title": "Tulevat tapahtumat",
  "home.cal.in": "kaupungissa",
  "home.cal.viewall": "Katso kaikki",
  "home.cal.unlockall": "Avaa kaikki tapahtumat",
  "home.coming": "Tulossa pian.",
  "home.aurora.country": "Suomi",
  "home.aurora.p1": "what on digitaalinen insiderisi kaupungin parhaisiin paikkoihin. Ei loputonta selailua, vaan huolella valikoituja tunnelmia, käsin poimittuja makuja ja unohtumattomia kokemuksia. Etsitpä sitten tyylikkäimpiä kahviloita, paikallisten suosittelemia viikonlopputapahtumia tai parhaita saunoja lähelläsi, olemme tehneet työn puolestasi.",
  "home.aurora.p2": "Helsingin sydämestä suomalaisiin helmiin — tuomme kaupungin näytöllesi. Seuraava seikkailusi alkaa tutulla kysymyksellä: mitä seuraavaksi?",

  // Common
  "common.featured": "Suositeltu",
  "common.tryagain": "Yritä uudelleen",
  "common.gohome": "Etusivulle",
  "common.prev": "Edellinen",
  "common.next": "Seuraava",

  // Error pages (root)
  "err.notfound.title": "Sivua ei löytynyt",
  "err.notfound.body": "Etsimääsi sivua ei ole olemassa tai se on siirretty.",
  "err.notfound.back": "Takaisin etusivulle",
  "err.boundary.title": "Tämä sivu ei latautunut",
  "err.boundary.body": "Jokin meni pieleen päässämme.",

  // Place detail
  "place.reviews": "Arvostelua",
  "place.write": "Kirjoita arvostelu",
  "place.reserve": "Varaa pöytä",
  "place.hideMap": "Piilota kartta",
  "place.showMap": "Sijainnit",
  "place.openMaps": "Avaa Google Mapsissa",
  "place.related": "Saatat myös pitää",
  "place.unlock.table": "Avaa ravintola",
  "place.unlock.activity": "Avaa aktiviteetti",
  "place.unlock.tickets": "Avaa liput",
  "place.unlock.now": "nyt",
  "place.unlock.label": "Avaa",
  "place.unlock.website": "Vieraile sivustolla",
  "place.notfound.title": "Ei löytynyt",
  "place.notfound.body": "Tätä paikkaa ei ole (vielä) olemassa.",
  "place.notfound.back": "Takaisin listaukseen",
  "place.error.title": "Jokin meni pieleen",
  "place.nearby": "Lähellä",
  "place.promo.eat": "Astu sisään kaupungin sydämeen!",
  "place.promo.do": "Unohtumaton kokemus odottaa!",
  "place.promo.happening": "Älä missaa tätä tapahtumaa!",
  "place.promo.body": "Hemmottele itseäsi — paikallisten, ystävien tai perheen kesken, tämä hetki on kokemisen arvoinen.",
  "place.body.tucked": "Sijaitsee sydämessä alueen",
  "place.body.tail": "yhdistää paikallisen käsityön ja vahvan tunnelman. Pikaisella vierailulla tai koko iltapäivän viipyen, voit odottaa tarkkuutta yksityiskohdissa, lämmintä vieraanvaraisuutta ja sellaisen hetken, jonka pariin haluat palata.",
  "place.body.hand": "Käsin valittu Whatin toimesta — jokainen paikka oppaassamme on paikallisten rakastama, testattu ja valmiina koettavaksi.",
  "place.priceFrom": "alk. 29€",
  "place.tagline.eat": "Makuelämys alueelta {area}, jota et unohda",
  "place.tagline.do": "Elämyksellinen hetki alueella {area}",
  "place.tagline.happening": "Tapahtuu {area}ssa — älä missaa",
  "place.tab.overview": "Yleistä",
  "place.tab.details": "Tiedot",
  "place.tab.tickets": "Liput",
  "place.tab.schedule": "Aikataulu",
  "place.tab.reviews": "Arvostelut",
  "place.tabShort.overview": "Info",
  "place.tabShort.details": "Lisää",
  "place.tabShort.tickets": "Liput",
  "place.tabShort.schedule": "Auki",
  "place.tabShort.reviews": "Arv.",
  "place.saved": "Tallennettu",
  "place.save": "Tallenna",
  "place.search.eat": "Etsi lisää ruokapaikkoja",
  "place.search.do": "Etsi lisää tekemistä",
  "place.search.happening": "Etsi lisää tapahtumia",

  // Cities
  "cities.choose": "Valitse kaupunkisi",
  "cities.title": "Kaupungit",
  "cities.lead": "Paikallisten valitsemat, reaaliajassa kartoitetut. Napauta nuppineulaa tai korttia vaihtaaksesi opasta.",
  "cities.current": "Nyt",
  "cities.liveMap": "Reaaliaikainen kartta",
  "cities.now": "Tutkit nyt · per.",
  "cities.people": "Asukkaita",
  "cities.region": "Alue",
  "cities.spots": "Paikkoja",
  "cities.jump.eat": "Syö",
  "cities.jump.do": "Tee",
  "cities.jump.live": "Nyt menossa",
  "cities.all": "Kaikki kaupungit",
  "cities.tap": "Napauta vaihtaaksesi",
  "cities.selected": "Valittu",
  "cities.select": "Valitse",
  "cities.curatedSpot": "valittu paikka",
  "cities.curatedSpots": "valittua paikkaa",
  "cities.founded": "Perustettu",
  "cities.explore": "Tutustu",
  "cities.hub": "Kaupunkihubi",
  "cities.directory": "Hakemisto",
  "cities.regions": "Alueet",
  "cities.fullMap": "Koko kartta",
  "cities.finland": "Suomi",

  // Saved
  "saved.eyebrow": "Kokoelmasi",
  "saved.title": "Tallennetut",
  "saved.empty": "Ei vielä tallennuksia",
  "saved.emptyBody": "Napauta sydäntä missä tahansa paikassa, kokemuksessa tai tapahtumassa tallentaaksesi sen tänne myöhempää varten.",
  "saved.start": "Aloita selaaminen",

  // Do experience page
  "do.eyebrow": "Tekemistä",
  "do.title.line1": "Vietä päivä",
  "do.title.line2": "kuin paikallinen.",
  "do.lead": "Paikallisia kävelyreittejä, rantapaikkoja, toreja ja hiljaisia nurkkia — hetkiä, jotka tekevät {city}sta omasi.",
  "do.stat.exp": "Kokemuksia",
  "do.stat.saunas": "Saunoja",
  "do.stat.rating": "Keskiarvo",
  "do.group.all": "Kaikki",
  "do.group.sauna": "Sauna & meri",
  "do.group.arch": "Arkkitehtuuri",
  "do.group.outdoor": "Ulkoilu & saaret",
  "do.group.shop": "Shoppailu",
  "do.spotlight.eyebrow": "Helsingin perusasia",
  "do.spotlight.title": "Saunarituaali",
  "do.spotlight.body": "Puulämmitteisiä piilopaikkoja, merikylpylöitä, julkisia saunatempppeleitä — valitse hikoilusi.",
  "do.section.eyebrow": "Käsin valitut",
  "do.section.all": "Kaikki tekeminen",
  "do.empty.title": "ei vielä mitään tällä kaistalla",
  "do.empty.body": "Kokeile toista suodatinta.",
  "do.cta.eyebrow": "Jatka tutkimista",
  "do.cta.title": "Onko jo nälkä?",
  "do.cta.body": "Yhdistä päiväsi kaupungin parhaisiin pöytiin, leipomoihin ja baareihin.",
  "do.cta.link": "Mihin syömään",
  "do.feature.discover": "Tutustu",

  // Happening experience page
  "hap.eyebrow": "Tällä viikolla",
  "hap.title.line1": "Mitä tapahtuu",
  "hap.title.line2": "Tänä iltana & myöhemmin.",
  "hap.lead": "Festivaalit, keikat, biennaalit ja hämyiset jazzhuoneet — {city}n kulttuurinen syke, viikoittain kuratoituna.",
  "hap.stat.live": "Live-tapahtumaa",
  "hap.stat.new": "Uutta tällä viikolla",
  "hap.stat.fest": "Festivaalikausi",
  "hap.stat.fest.value": "Elo",
  "hap.lane.all": "Kaikki tapahtumat",
  "hap.lane.music": "Musiikki & festivaalit",
  "hap.lane.art": "Taide & näyttelyt",
  "hap.lane.live": "Live & yöelämä",
  "hap.section.eyebrow": "Kalenterissa",
  "hap.section.all": "Tulossa",
  "hap.empty.title": "Ei vielä tapahtumia tällä kaistalla",
  "hap.empty.body": "Kokeile toista kategoriaa.",
  "hap.cta.eyebrow": "Viikon poiminta",
  "hap.cta.title": "Älä missaa Helsingin iltaa.",
  "hap.cta.body": "Viikon parhaat keikat, avajaiset ja tapahtumat — suoraan paikallisilta.",
  "hap.cta.link": "Suunnittele myös päiväsi",
  "hap.local": "Paikallisten suosikki",
  "hap.new": "Uusi",
  "hap.tickets": "Liput",
  "hap.gettickets": "Hanki liput",

  // Contact page extras
  "contact.eyebrow.reply": "Vastaamme 48 tunnissa",
  "contact.eyebrow.in": "what Finland Oy · Ota yhteyttä",
  "contact.hero.line1": "Aloitetaan",
  "contact.hero.line2": "keskustelu.",
  "contact.lead": "Yhteistyökumppanuudet, lehdistö tai vinkit paikoista, jotka meidän pitäisi tuntea — laita viestiä. Jokainen viesti ohjautuu oikealle henkilölle tiimissämme.",
  "contact.tile.general": "Yleinen",
  "contact.tile.part": "Yhteistyö",
  "contact.tile.based": "Sijainti",
  "contact.tile.basedV": "Helsinki, Suomi",
  "contact.tile.hours": "Aukioloajat",
  "contact.tile.hoursV": "Ma–Pe · 09–17 EET",
  "contact.side.eyebrow": "Miksi ottaa yhteyttä",
  "contact.side.title.a": "Tiimi nimen takana",
  "contact.side.b1": "Kumppanuus kampanjassa",
  "contact.side.b2": "Ehdota paikkaa, josta meidän pitäisi kertoa",
  "contact.side.b3": "Lehdistö, media tai haastattelupyynnöt",
  "contact.side.b4": "Bugiraportit & ominaisuusideat",
  "contact.side.follow": "Seuraa meitä",
  "contact.send.eyebrow": "Lähetä viesti",
  "contact.send.title": "Kerro mitä mielessäsi on",
  "contact.cta.eyebrow": "Haluatko ensin selata?",
  "contact.cta.title": "Tutustu mitä syödä, tehdä ja nähdä.",
  "contact.cta.link": "Avaa opas",

  "hero.ask": "Kysy whatAI:lta",
  "hero.ask.loading": "avataan…",
  "hero.ask.aria": "kysy whatAI:lta",
  "hero.rateGoogle": "arvostele meidät googlessa",
  "ai.prompt.gems": "Kysy whatAI:lta — Helsingin piilotetut helmet",
  "ai.prompt.dinner": "Kysy whatAI:lta — parhaat illalliset tänään",
  "ai.prompt.sauna": "Kysy whatAI:lta — rentouttava saunahetki",
  "ai.prompt.weekend": "Kysy whatAI:lta — mitä tapahtuu viikonloppuna",
  "ai.prompt.cafe": "Kysy whatAI:lta — kodikkaita kahviloita työskentelyyn",
  "ai.prompt.today": "Kysy whatAI:lta — tekemistä tänään",

  "home.sauna.title": "Saunahetki, mitä?! 🔥",
  "home.sauna.cta": "Löydä lisää saunoja",
  "home.events.subtitle": "kaupungissa",
  "home.spots.suffix": "kaupungissa",
  "home.aurora.title": "Seuraava seikkailusi alkaa tästä",
  "home.aurora.subtitle": "Henkilökohtainen oppaasi auttaa sinua löytämään kaupungin parhaat paikat ja tapahtumat.",
  "home.hero.subheadline": "Kuratoidut ravintolat, saunat ja tapahtumat.",
  "home.hero.badge": "120+ käsin valittua paikkaa kaupungissa",

  "card.noreviews": "Ei vielä arvosteluja",
  "card.unlock": "Avaa",

  "event.ongoing": "Käynnissä",
  "event.allDay": "Koko päivän · ovet 18:00",
};


export const UI: Record<LangCode, Partial<Record<UIKey, string>>> = {
  en: UI_EN,
  // Manual translations override AI-generated ones key-by-key.
  fi: { ...(GEN_FI as Partial<Record<UIKey, string>>), ...UI_FI },
};
